import type { PaginatedResponse, Paginator } from "./auto-types";
type json_object = null | string | number | { [key: string]: json_object };
export type QueryParamValue = string | number | null | undefined;
export type QueryParams = Record<string, QueryParamValue>;

const low_level = async <T>(
  url: string,
  method: string,
  get_params?: QueryParams,
  data?: json_object,
): Promise<T> => {
  const extra = !!data ? { body: JSON.stringify(data) } : {};

  const options = {
    method: method,
    credentials: "include" as const,
    headers: { "Content-Type": "application/json" },
    ...extra,
  };
  const params = get_params
    ? Object.entries(get_params).filter(([, value]) => value !== null && value !== undefined)
    : [];
  const url_append =
    params.length > 0
      ? `?${new URLSearchParams(params.map(([key, value]) => [key, String(value)])).toString()}`
      : "";

  const response = await fetch(`${url}${url_append}`, options);
  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error(typeof payload === "string" ? payload : JSON.stringify(payload));
  }
  return payload as T;
};

export const get_api = async <T>(url: string, get_params?: QueryParams): Promise<T> =>
  low_level<T>(url, "GET", get_params, undefined);
export const post_api = async <T>(url: string, data: json_object): Promise<T> =>
  low_level<T>(url, "POST", undefined, data);
export const delete_api = async <T>(url: string, get_params?: QueryParams): Promise<T> =>
  low_level<T>(url, "DELETE", get_params, undefined);
export const patch_api = async <T>(url: string, data: json_object): Promise<T> =>
  low_level<T>(url, "PATCH", undefined, data);

export const get_page = async <T>(
  base_url: string,
  page_num: number,
  get_params?: QueryParams & Partial<Omit<Paginator, "page">>,
): Promise<PaginatedResponse<T>> => {
  const { page_size, ...rest } = get_params ?? {};
  const params: QueryParams = {
    ...rest,
    page: page_num,
  };
  if (page_size !== undefined && page_size !== null) {
    params.page_size = page_size;
  }
  return get_api<PaginatedResponse<T>>(base_url, params);
};

export const get_all_model_pages = async <T>(
  url: string,
  get_params?: QueryParams,
): Promise<T[]> => {
  const first = await get_page<T>(url, 1, get_params);
  let ret = first.items;

  for (let page = 2; page <= first.total_pages; page++) {
    const others = await get_page<T>(url, page, get_params);
    ret = ret.concat(others.items);
  }

  return ret;
};

export const getWsUrl = (path: string, baseUrl?: string) => {
  const url = baseUrl ? new URL(baseUrl) : window.location;
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${url.host}${path}`;
};

/**
 * Websocket to a push endpoint that reconnects forever, backing off
 * exponentially up to five minutes — a rejected handshake (signed-out
 * session) retries like a lost connection. `baseUrl` (default: the page's
 * own origin) is re-read on every attempt; while it throws (config still
 * loading) retries ride a short fuse instead. A failure persisting past a
 * few attempts is surfaced once on the console. Returns a cancel function.
 */
export const connectReconnectingSocket = <T>(
  path: string,
  onMessage: (message: T) => void,
  baseUrl?: () => string,
  reconnectMs = 5_000,
): (() => void) => {
  const maxReconnectMs = 300_000;
  const configRetryMs = 250;
  const failuresBeforeWarning = 3;
  let cancelled = false;
  let ws: WebSocket | undefined;
  let retryTimerId: number | undefined;
  let failures = 0;

  const scheduleRetry = (reason: unknown, baseMs: number) => {
    if (cancelled) {
      return;
    }
    failures += 1;
    if (failures === failuresBeforeWarning) {
      console.warn(`Socket ${path} keeps failing, retrying with backoff.`, reason);
    }
    const delayMs = Math.min(baseMs * 2 ** (failures - 1), maxReconnectMs);
    retryTimerId = window.setTimeout(connect, delayMs);
  };

  const connect = () => {
    let url: string;
    try {
      url = getWsUrl(path, baseUrl?.());
    } catch (error) {
      scheduleRetry(error, configRetryMs);
      return;
    }
    ws = new WebSocket(url);
    ws.onopen = () => {
      failures = 0;
    };
    ws.onmessage = event => {
      let message: T;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!cancelled) {
        onMessage(message);
      }
    };
    ws.onclose = event => {
      scheduleRetry(event, reconnectMs);
    };
  };
  connect();

  return () => {
    cancelled = true;
    if (retryTimerId !== undefined) {
      window.clearTimeout(retryTimerId);
    }
    ws?.close();
  };
};
